import { useEffect, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { Button } from "../../tailwind/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../tailwind/dialog";
import { CONTACTS_PRIMARY_BUTTON_CLASS } from "../../../pages/whatsapp/data/contactsData";
import {
  getImportPreviewRows,
  guessColumnMapping,
  parseContactImportFile,
  PREVIEW_ROW_LIMIT,
} from "../../../pages/whatsapp/utils/contactsImportUtils";
import { validateImportContactsRows } from "../../../pages/whatsapp/utils/contactsValidation";

function resetImportState() {
  return {
    selectedFile: null,
    headers: [],
    rows: [],
    mapping: { phone: "", name: "", email: "" },
    parseError: "",
    submitError: "",
    isParsing: false,
  };
}

function formatCellValue(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

export default function ImportContactsDialog({
  open,
  onOpenChange,
  importing,
  onSubmit,
}) {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({ phone: "", name: "", email: "" });
  const [parseError, setParseError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isParsing, setIsParsing] = useState(false);

  const hasParsedFile = Boolean(selectedFile && headers.length);
  const previewRows = getImportPreviewRows(rows);
  const showingPartialPreview = rows.length > PREVIEW_ROW_LIMIT;
  const isBusy = importing || isParsing;

  useEffect(() => {
    if (open) return;
    const next = resetImportState();
    setSelectedFile(next.selectedFile);
    setHeaders(next.headers);
    setRows(next.rows);
    setMapping(next.mapping);
    setParseError(next.parseError);
    setSubmitError(next.submitError);
    setIsParsing(next.isParsing);
  }, [open]);

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && isBusy) return;
    onOpenChange(nextOpen);
  };

  const handlePickFile = () => {
    if (isBusy) return;
    fileInputRef.current?.click();
  };

  const processFile = async (file) => {
    if (!file || isBusy) return;

    setParseError("");
    setSubmitError("");
    setIsParsing(true);

    try {
      const parsed = await parseContactImportFile(file, "csv");
      const nextMapping = guessColumnMapping(parsed.headers);
      const rowsError = validateImportContactsRows(parsed.rows, nextMapping);

      setSelectedFile(file);
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setMapping(nextMapping);

      if (rowsError) {
        setParseError(rowsError);
      }
    } catch (error) {
      setSelectedFile(null);
      setHeaders([]);
      setRows([]);
      setMapping({ phone: "", name: "", email: "" });
      setParseError(
        error?.message || "Could not read CSV file. Check the format and try again."
      );
    } finally {
      setIsParsing(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    await processFile(file);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isBusy || hasParsedFile) return;
    const file = event.dataTransfer?.files?.[0];
    await processFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleBack = () => {
    if (isBusy) return;

    if (hasParsedFile) {
      setSelectedFile(null);
      setHeaders([]);
      setRows([]);
      setMapping({ phone: "", name: "", email: "" });
      setParseError("");
      setSubmitError("");
      return;
    }

    handleOpenChange(false);
  };

  const handleSubmit = () => {
    if (!selectedFile) {
      setSubmitError("Select a CSV file before importing.");
      return;
    }

    const validationError = validateImportContactsRows(rows, mapping);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setSubmitError("");
    // API integration comes next — parent currently receives validated file + mapping.
    onSubmit({ file: selectedFile, mapping });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-[calc(100vw-2rem)] max-w-3xl flex-col gap-0 overflow-hidden bg-white p-0 dark:bg-slate-900 border-gray-200 dark:border-slate-800 sm:w-full">
        <div className="shrink-0 space-y-1.5 border-b border-gray-200 px-6 py-5 dark:border-slate-800">
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle>Import Contacts</DialogTitle>
            <DialogDescription>
              {hasParsedFile
                ? "Review your CSV preview below, then continue."
                : "Upload a CSV file with a header row. Phone column is required."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleFileChange}
        />

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <div className="py-2 space-y-4">
            {!hasParsedFile ? (
              <button
                type="button"
                onClick={handlePickFile}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                disabled={isBusy}
                className="w-full border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-xl p-8 flex flex-col items-center justify-center text-center hover:border-emerald-500 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20 transition-colors disabled:opacity-60"
              >
                <Upload className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {isParsing ? "Reading CSV…" : "Click to upload or drag and drop"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  CSV files only · max 5 MB
                </p>
              </button>
            ) : (
              <>
                <div className="rounded-lg border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-950/50 px-3 py-2 text-sm text-gray-700 dark:text-slate-300">
                  <p
                    className="truncate font-medium text-gray-900 dark:text-white"
                    title={selectedFile.name}
                  >
                    {selectedFile.name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {rows.length} row{rows.length === 1 ? "" : "s"} ·{" "}
                    {headers.length} column{headers.length === 1 ? "" : "s"}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
                    <span>
                      Phone:{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {mapping.phone || "Not detected"}
                      </span>
                    </span>
                    <span>
                      Name:{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {mapping.name || "—"}
                      </span>
                    </span>
                    <span>
                      Email:{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {mapping.email || "—"}
                      </span>
                    </span>
                  </div>
                </div>

                {showingPartialPreview ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Showing first {PREVIEW_ROW_LIMIT} of {rows.length} rows
                  </p>
                ) : null}

                <div className="min-w-0 overflow-x-auto overflow-y-auto max-h-[min(50vh,20rem)] rounded-lg border border-gray-200 dark:border-slate-800">
                  <table className="w-max min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-slate-950/80">
                      <tr>
                        {headers.map((header) => (
                          <th
                            key={header}
                            className="max-w-[11rem] truncate px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-400"
                            title={header}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className="border-t border-gray-100 dark:border-slate-800"
                        >
                          {headers.map((header) => {
                            const cellValue = formatCellValue(row[header]);
                            return (
                              <td
                                key={`${rowIndex}-${header}`}
                                className="max-w-[11rem] truncate px-3 py-2 text-gray-900 dark:text-slate-200"
                                title={cellValue}
                              >
                                {cellValue}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {parseError ? (
              <p className="text-sm text-red-600 dark:text-red-400">{parseError}</p>
            ) : null}
            {submitError ? (
              <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
            ) : null}
          </div>
        </div>

        <div className="shrink-0 border-t border-gray-200 px-6 py-4 dark:border-slate-800">
          <DialogFooter className="sm:justify-end">
            <Button type="button" variant="ghost" onClick={handleBack} disabled={isBusy}>
              {hasParsedFile ? "Back" : "Cancel"}
            </Button>
            {hasParsedFile ? (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isBusy || Boolean(parseError)}
                className={CONTACTS_PRIMARY_BUTTON_CLASS}
              >
                {importing ? "Importing…" : "Import contacts"}
              </Button>
            ) : null}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
